import { useMemo, useState, useEffect } from 'react';
import { useProducts } from './useProducts';
import { useProductFiltering } from './useProductFiltering';
import { useProductModals } from './useProductModals';
import { useBulkSelection } from '../../../hooks/useBulkSelection';
import { useCsvExport } from '../../../hooks/useCsvExport';
import { ProductDTO } from '../../../services/modules/productService';
import { notify } from '../../../utils/toast';

export const useProductsPage = () => {
    // 1. Core Data
    const {
        products,
        loading,
        createProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
        refreshProducts
    } = useProducts();

    // 2. Export Utility
    const { exportData } = useCsvExport();

    // 3. Filtering Logic
    const {
        searchValue,
        setSearchValue,
        filterName,
        setFilterName,
        filterOperator,
        setFilterOperator,
        filterMinValue,
        setFilterMinValue,
        filterMaxValue,
        setFilterMaxValue,
        filteredProducts,
        hasActiveFilters
    } = useProductFiltering(products);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const paginatedProducts = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredProducts.slice(start, start + pageSize);
    }, [currentPage, filteredProducts, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, filterName, filterOperator, filterMinValue, filterMaxValue]);

    // 4. Bulk Selection Logic
    const {
        selectedIds,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,
        getSelectedItems
    } = useBulkSelection(filteredProducts, paginatedProducts);

    // 5. Modals State
    const modals = useProductModals();

    // --- Action Handlers ---

    const handleSaveProduct = async (productData: { name: string; value: number; description: string }) => {
        try {
            if (modals.productToEdit) {
                await updateProduct(modals.productToEdit.id, productData);
                notify.success('Produto atualizado com sucesso!');
            } else {
                await createProduct(productData);
                notify.success('Produto criado com sucesso!');
            }
            modals.closeModals();
        } catch (error) {
            console.error('Failed to save product:', error);
            notify.error('Erro ao salvar produto.');
        }
    };

    const handleConfirmDelete = async () => {
        if (modals.productToDelete) {
            try {
                await deleteProduct(modals.productToDelete.id);
                notify.success('Produto excluído com sucesso!');
            } catch (error) {
                console.error('Failed to delete product:', error);
                notify.error('Erro ao excluir produto.');
            }
        }
        modals.closeModals();
    };

    const handleBulkDelete = async () => {
        const selectedProducts = getSelectedItems();
        try {
            // Executa sequencialmente para garantir (poderia ser Promise.all se suportado pelo backend)
            for (const product of selectedProducts) {
                await deleteProduct(product.id);
            }
            notify.success(`${selectedProducts.length} produtos excluídos.`);
            clearSelection();
            modals.setBulkDeleteModalOpen(false);
        } catch (error) {
            console.error('Failed to delete products:', error);
            notify.error('Erro ao excluir produtos em massa.');
        }
    };

    const handleDuplicateProduct = async (product: ProductDTO) => {
        try {
            await duplicateProduct(product);
            notify.success('Produto duplicado com sucesso!');
        } catch (error) {
            console.error('Failed to duplicate product:', error);
            notify.error('Erro ao duplicar produto.');
        }
    };

    const handleExport = () => {
        if (products && products.length > 0) {
            const productsToExport = products.map(({ name, value, description }) => ({
                'Nome': name,
                'Valor (R$)': value.toFixed(2).replace('.', ','),
                'Descrição': description || '',
            }));
            exportData(productsToExport, 'produtos.xls');
            notify.success('Exportação iniciada!');
        } else {
            notify.warning('Nenhum produto para exportar.');
        }
    };

    const handleImport = async (file: File) => {
        // Placeholder conforme código original
        try {
            notify.info('Importação em desenvolvimento...');
        } catch (error) {
            console.error('Failed to import products:', error);
            notify.error('Erro na importação.');
        }
    };

    const handleApplyFilters = (
        name: string,
        operator: 'equals' | 'contains',
        min?: number,
        max?: number
    ) => {
        setFilterName(name);
        setFilterOperator(operator);
        setFilterMinValue(min);
        setFilterMaxValue(max);
    };

    return {
        // Data & Loading
        filteredProducts,
        paginatedProducts,
        loading,
        refreshProducts,

        // Pagination
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,

        // Selection
        selectedIds,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,

        // Filtering State & Setters
        searchValue,
        setSearchValue,
        hasActiveFilters,

        // Filter Values for Modal
        filterValues: {
            name: filterName,
            operator: filterOperator,
            min: filterMinValue,
            max: filterMaxValue
        },

        // Modals State
        modals,

        // Actions
        handleSaveProduct,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicateProduct,
        handleExport,
        handleImport,
        handleApplyFilters
    };
};
